import { useEffect, useRef, useCallback, useState } from 'react';
import useAuth from './useAuth';
import { useEventTracking } from './useEventTracking';
import { refreshAccessToken } from 'src/utils/axios';
import { isTokenMetaValid } from 'src/utils/jwt';

// A connection that drops within this window is treated as short-lived and
// must consume (not refund) the retry budget on every reconnect.
const SHORT_LIVED_THRESHOLD_MS = 5000;

interface WebSocketMessage {
  type: string;
  data?: unknown;
  message?: string;
  timestamp?: string;
}

interface MessageHandler {
  [key: string]: (data: unknown) => void;
}

interface UseWebSocketOptions {
  enabled?: boolean;
  url: string;
  messageHandlers?: MessageHandler;
  genericHandler?: (eventType: string, data: unknown) => void;
  onConnect?: () => void;
  onDisconnect?: (code: number, reason: string) => void;
  onError?: (error: Event) => void;
  reconnectAttempts?: number;
  reconnectDelay?: number;
}

export const useWebSocket = (
  options: UseWebSocketOptions
): {
  isConnected: boolean;
  connectionError: string | null;
  reconnectAttemptsCount: number;
  reconnectStopped: boolean;
  connect: () => void;
  disconnect: () => void;
  sendMessage: (message: unknown) => void;
  sendBinaryMessage: (data: ArrayBuffer) => void;
  resetConnection: () => void;
} => {
  const { user } = useAuth();
  const { trackError } = useEventTracking();

  const {
    enabled = true,
    url,
    messageHandlers = {},
    genericHandler,
    onConnect,
    onDisconnect,
    onError,
    reconnectAttempts = 5,
    reconnectDelay = 3000,
  } = options;

  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const [reconnectAttemptsCount, setReconnectAttemptsCount] = useState(0);
  const [reconnectStopped, setReconnectStopped] = useState(false);

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const isActiveRef = useRef(true);
  const openedAtRef = useRef<number | null>(null);
  const shortLivedStreakRef = useRef(0);
  const reconnectAttemptsCountRef = useRef(0);
  const messageHandlersRef = useRef(messageHandlers);
  const connectRef = useRef<(() => void) | null>(null);

  // Update message handlers ref when they change
  messageHandlersRef.current = messageHandlers;

  // Recover from an auth rejection by refreshing the access token and
  // reconnecting. Only attempts this when the session has a live refresh token
  // (a real cookie-authenticated login) — renter/self-reg token flows carry no
  // refresh token, so their rejection is permanent and must not loop. Recovery
  // consumes the same reconnect budget as normal reconnects, bounded by
  // reconnectAttempts.
  const recoverFromAuthRejection = useCallback(() => {
    let refreshable = false;
    try {
      refreshable = isTokenMetaValid('refreshTokenMetadata');
    } catch {
      // Corrupt localStorage metadata must not break the close handler.
      refreshable = false;
    }

    if (!refreshable) {
      isActiveRef.current = false;
      setReconnectStopped(true);
      trackError(new Error('WebSocket authentication rejected'), {
        context: 'websocket_connection',
        action: 'auth_rejected',
      });
      return;
    }

    const newAttempts = reconnectAttemptsCountRef.current + 1;
    reconnectAttemptsCountRef.current = newAttempts;
    setReconnectAttemptsCount(newAttempts);

    if (newAttempts >= reconnectAttempts) {
      setConnectionError(
        `Failed to reconnect after ${reconnectAttempts} attempts. Connection stopped.`
      );
      isActiveRef.current = false;
      setReconnectStopped(true);
      trackError(new Error('WebSocket authentication recovery failed'), {
        context: 'websocket_connection',
        action: 'max_reconnect_attempts_reached',
      });
      return;
    }

    refreshAccessToken()
      .then(() => {
        // The component may have unmounted while the refresh was in flight
        // (cleanup sets isActiveRef.current = false); don't resurrect the
        // socket after unmount.
        if (!isActiveRef.current) {
          return;
        }
        shortLivedStreakRef.current = 0;
        isActiveRef.current = true;
        setReconnectStopped(false);
        connectRef.current?.();
      })
      .catch(() => {
        if (!isActiveRef.current) {
          return;
        }
        isActiveRef.current = false;
        setReconnectStopped(true);
        trackError(new Error('WebSocket authentication refresh failed'), {
          context: 'websocket_connection',
          action: 'auth_refresh_failed',
        });
      });
  }, [reconnectAttempts, trackError]);

  const connect = useCallback(() => {
    if (
      !user ||
      !enabled ||
      wsRef.current?.readyState === WebSocket.OPEN ||
      !url
    ) {
      return;
    }

    if (
      reconnectAttemptsCountRef.current >= reconnectAttempts ||
      shortLivedStreakRef.current >= reconnectAttempts
    ) {
      console.warn(
        'Connection stopped due to max reconnection attempts reached'
      );
      setConnectionError(
        `Failed to reconnect after ${reconnectAttempts} attempts. Connection stopped.`
      );
      isActiveRef.current = false;
      setReconnectStopped(true);
      return;
    }

    try {
      const root_url = new URL(
        import.meta.env.VITE_API_ROOT || 'http://localhost:8000/api/'
      );
      const baseUrl = root_url.origin;
      const wsUrl = baseUrl
        .replace(/^https:\/\//, 'wss://')
        .replace(/^http:\/\//, 'ws://');
      const fullUrl = `${wsUrl}/ws/${url}`;

      const ws = new WebSocket(fullUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        // A durable open (no short-lived streak behind it) proves the link is
        // healthy: refund the retry budget. A short-lived open must NOT reset
        // the budget, or an open-then-die socket would never exhaust it.
        if (shortLivedStreakRef.current === 0) {
          reconnectAttemptsCountRef.current = 0;
          setReconnectAttemptsCount(0);
        }
        setReconnectStopped(false);
        openedAtRef.current = Date.now();
        setIsConnected(true);
        setConnectionError(null);
        isActiveRef.current = true;

        // Call onConnect callback
        if (onConnect) {
          onConnect();
        }
      };

      ws.onmessage = async (event) => {
        try {
          let data;
          if (event.data instanceof ArrayBuffer) {
            data = event.data;
          } else if (event.data instanceof Blob) {
            // Convert Blob to ArrayBuffer
            data = await event.data.arrayBuffer();
          }
          // Handle binary messages
          if (data) {
            try {
              const view = new DataView(data);
              if (view.byteLength < 1) {
                console.error('Invalid packaged');
                return;
              }
              const messageType = view.getUint8(0);

              if (messageType === 1) {
                // Mouse movement binary
                // Supported formats:
                //  - 13 bytes: 1(type) + 4(user_id) + 4(x float32) + 4(y float32)
                //  - 17 bytes: 1(type) + 4(user_id) + 4(x float32) + 4(y float32) + 4(scroll float32)
                if (view.byteLength === 13 || view.byteLength === 17) {
                  const userId = view.getUint32(1);
                  const x = view.getFloat32(5);
                  const y = view.getFloat32(9);

                  // Call generic handler with binary mouse data
                  if (genericHandler) {
                    genericHandler('lv_mouse_moved', { user_id: userId, x, y });
                  } else if (messageHandlersRef.current['lv_mouse_moved']) {
                    messageHandlersRef.current['lv_mouse_moved']({
                      user_id: userId,
                      x,
                      y,
                    });
                  }
                } else {
                  console.warn(
                    'Invalid binary mouse message size:',
                    view.byteLength
                  );
                }
              } else {
                console.warn('Unknown binary message type:', messageType);
              }
            } catch (error) {
              console.error('Error parsing binary message:', error);
            }
            return;
          }

          // Handle JSON messages
          const message: WebSocketMessage = JSON.parse(event.data);

          // Handle built-in message types
          switch (message.type) {
            case 'ping':
              // Server pinged us - respond with pong
              ws.send(JSON.stringify({ type: 'pong' }));
              break;
            case 'pong':
              // Server sent pong - no action needed
              break;
            case 'error':
              console.error('WebSocket error:', message.message);
              setConnectionError(message.message || 'Unknown WebSocket error');
              if (onError) {
                onError(new Event('websocket_error'));
              }
              break;
            default:
              // Use genericHandler if provided, otherwise fall back to messageHandlers
              if (genericHandler) {
                // Handle both message.data and message.message for backward compatibility
                const data = message.data || message.message;
                genericHandler(message.type, data);
              } else if (messageHandlersRef.current[message.type]) {
                const data = message.data || message.message;
                messageHandlersRef.current[message.type](data);
              } else {
                console.error(
                  'Unhandled WebSocket message type:',
                  message.type
                );
              }
          }
        } catch (error) {
          console.error('Failed to parse WebSocket message:', error);
          trackError(error, {
            context: 'websocket_message_parsing',
            action: 'parse_error',
          });
        }
      };

      ws.onclose = (event) => {
        const wasOpened = openedAtRef.current !== null;
        const lifetimeMs = wasOpened ? Date.now() - openedAtRef.current : 0;
        openedAtRef.current = null;

        // Track a short-lived streak: consecutive connections that drop within
        // the threshold. A durable close resets the streak to 0.
        if (event.code !== 1000 && event.code !== 4001) {
          if (lifetimeMs < SHORT_LIVED_THRESHOLD_MS) {
            shortLivedStreakRef.current += 1;
          } else {
            shortLivedStreakRef.current = 0;
          }
        }

        console.debug('WebSocket closed:', {
          code: event.code,
          reason: event.reason,
          wasClean: event.wasClean,
          lifetimeMs,
          shortLivedStreak: shortLivedStreakRef.current,
          reconnectAttempts: reconnectAttemptsCountRef.current,
          maxAttempts: reconnectAttempts,
        });
        setIsConnected(false);

        // Call onDisconnect callback
        if (onDisconnect) {
          onDisconnect(event.code, event.reason);
        }

        // Auth rejection: either a post-open close 4001, or a rejected
        // handshake (onerror fired and the socket closed with 1006 without
        // ever opening). Try to recover by refreshing the access token — but
        // only when the session is refreshable (see recoverFromAuthRejection).
        const authRejected =
          event.code === 4001 || (!wasOpened && event.code === 1006);
        if (authRejected) {
          setConnectionError(
            'WebSocket connection rejected (authentication failed)'
          );
          recoverFromAuthRejection();
          return;
        }

        // Normal closure or the connection was already stopped: no reconnect.
        if (event.code === 1000 || !isActiveRef.current) {
          return;
        }

        // A run of short-lived opens consumes the retry budget exactly like
        // failed connection attempts: give up once the streak reaches the
        // configured attempt limit.
        if (
          shortLivedStreakRef.current >= reconnectAttempts ||
          reconnectAttemptsCountRef.current >= reconnectAttempts
        ) {
          setConnectionError(
            `Failed to reconnect after ${reconnectAttempts} attempts. Connection stopped.`
          );
          isActiveRef.current = false;
          setReconnectStopped(true);
          trackError(new Error('WebSocket reconnection failed'), {
            context: 'websocket_connection',
            action: 'max_reconnect_attempts_reached',
            attempts: reconnectAttemptsCountRef.current,
          });
          return;
        }

        const newAttempts = reconnectAttemptsCountRef.current + 1;
        reconnectAttemptsCountRef.current = newAttempts;
        setReconnectAttemptsCount(newAttempts);

        reconnectTimeoutRef.current = setTimeout(() => {
          if (isActiveRef.current && newAttempts < reconnectAttempts) {
            connect();
          } else if (newAttempts >= reconnectAttempts) {
            setConnectionError(
              `Failed to reconnect after ${reconnectAttempts} attempts. Connection stopped.`
            );
            isActiveRef.current = false;
            setReconnectStopped(true);
            trackError(new Error('WebSocket reconnection failed'), {
              context: 'websocket_connection',
              action: 'max_reconnect_attempts_reached',
              attempts: newAttempts,
            });
          }
        }, reconnectDelay * newAttempts);
      };

      ws.onerror = (error) => {
        // An error before the socket ever opened means the handshake was
        // rejected (auth etc.). The subsequent onclose (code 1006) drives the
        // recovery path, so only record the error here.
        const handshakeRejected = openedAtRef.current === null;
        if (handshakeRejected) {
          setConnectionError('WebSocket connection rejected');
          trackError(new Error('WebSocket handshake rejected'), {
            context: 'websocket_connection',
            action: 'handshake_rejected',
          });
        } else {
          setConnectionError('WebSocket connection error');
          trackError(new Error('WebSocket connection error'), {
            context: 'websocket_connection',
            action: 'websocket_error',
          });
        }

        if (onError) {
          onError(error);
        }
      };
    } catch (error) {
      console.error('Failed to create WebSocket connection:', error);
      setConnectionError('Failed to create WebSocket connection');
      trackError(error, {
        context: 'websocket_connection',
        action: 'connection_creation_failed',
      });
    }
  }, [
    user,
    enabled,
    url,
    reconnectAttempts,
    onConnect,
    genericHandler,
    onError,
    trackError,
    onDisconnect,
    reconnectDelay,
    recoverFromAuthRejection,
  ]);

  // Keep a ref to the latest connect so recoverFromAuthRejection can trigger a
  // fresh connection without creating a circular useCallback dependency.
  useEffect(() => {
    connectRef.current = connect;
  }, [connect]);

  const disconnect = useCallback(() => {
    isActiveRef.current = false;

    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    // No ping interval to clear

    if (wsRef.current) {
      wsRef.current.close(1000, 'Client disconnecting');
      wsRef.current = null;
    }

    setIsConnected(false);
    setConnectionError(null);
    reconnectAttemptsCountRef.current = 0;
    setReconnectAttemptsCount(0);
  }, []);

  const sendMessage = useCallback((message: unknown) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message));
    } else {
      console.warn('WebSocket is not connected. Cannot send message:', message);
    }
  }, []);

  const sendBinaryMessage = useCallback((data: ArrayBuffer) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      try {
        wsRef.current.send(data);
      } catch (error) {
        console.error('Error sending binary message:', error);
        setConnectionError('Failed to send binary message');
      }
    } else {
      console.warn(
        'WebSocket is not connected. Cannot send binary message. State:',
        wsRef.current?.readyState
      );
    }
  }, []);

  const resetConnection = useCallback(() => {
    reconnectAttemptsCountRef.current = 0;
    setReconnectAttemptsCount(0);
    setConnectionError(null);
    setReconnectStopped(false);
    shortLivedStreakRef.current = 0;
    isActiveRef.current = true;
    disconnect();
    if (user && enabled && url) {
      setTimeout(() => connect(), 1000); // Wait 1 second before reconnecting
    }
  }, [disconnect, connect, user, enabled, url]);

  // Connect when user is available and enabled
  useEffect(() => {
    if (user && enabled && url) {
      connect();
    }

    return () => {
      disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, enabled, url]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    isConnected,
    connectionError,
    reconnectAttemptsCount,
    reconnectStopped,
    connect,
    disconnect,
    sendMessage,
    sendBinaryMessage,
    resetConnection,
  };
};
